import React from 'react';
import { Flex, Box, Text } from 'rebass';
import { Formik, FormikConfig } from 'formik';
import * as Yup from 'yup';

import {
  ButtonLarge,
  TermsAndConditions,
  FormHeader,
  FormSubHeader,
  CreateAnAccount,
  ErrorNotification,
  EmailField,
  useWindowSize,
} from '@mdlinx/component-library';
import { FormContainer } from 'app/components/layout/form-container';

export type SignUpFormAcquisitionProps = {
  onSubmit: FormikConfig<{
    email: string;
  }>['onSubmit'];
  errorMessage?: string;
  onCreateAnAccountClick: () => void;
  initialEmail: string;
  userName?: string;
  onLoginLinkClicked: () => void;
  header: string;
  subHeader: string;
};

export const SignUpFormAcquisition = ({
  onSubmit,
  errorMessage,
  onCreateAnAccountClick,
  initialEmail = '',
  userName,
  header,
  subHeader,
}: SignUpFormAcquisitionProps) => {
  const { windowWidth } = useWindowSize();
  const submitStarted = React.useRef(false);

  return (
    <FormContainer alternativeScene="login">
      <Flex flexDirection="column" alignItems="center">
        <FormHeader>{header}</FormHeader>
        <Box mt="12px" />
        <Text maxWidth="370px" textAlign="center">
          <FormSubHeader>{subHeader}</FormSubHeader>
        </Text>
        <Box width="100%" mt="20px" mb={['17px', '0px']} backgroundColor="white">
          {errorMessage && (
            <Box my="12px">
              <ErrorNotification errorMessage={errorMessage} />
            </Box>
          )}
          <Formik
            initialValues={{ email: initialEmail }}
            enableReinitialize={true}
            validationSchema={Yup.object({
              email: Yup.string()
                .email('Invalid email address')
                .required('Required'),
            })}
            onSubmit={onSubmit}
            validateOnMount={false}
          >
            {formik => {
              const handleSubmitButtonClick = (event: React.SyntheticEvent) => {
                event.preventDefault();
                event.stopPropagation();
                submitStarted.current = true;
                formik.handleSubmit();
              };
              return (
                <form
                  onSubmit={e => {
                    submitStarted.current = true;
                    formik.handleSubmit(e);
                  }}
                >
                  <EmailField
                    withButton={windowWidth > 480}
                    isSubmitButtonDisabled={!formik.isValid}
                    onSubmitClicked={handleSubmitButtonClick}
                    isLoading={formik.isSubmitting}
                    submitStarted={submitStarted}
                  />
                  <Box sx={{ display: ['block', 'none'] }}>
                    <ButtonLarge
                      type="submit"
                      disabled={!formik.isValid}
                      isLoading={formik.isSubmitting}
                      onClick={handleSubmitButtonClick}
                    >
                      Sign Up
                    </ButtonLarge>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
        {userName && (
          <>
            <CreateAnAccount userName={userName} onClick={onCreateAnAccountClick} />
            <Box mt={['16px', '16px', '30px']} />
          </>
        )}
        <TermsAndConditions />
      </Flex>
    </FormContainer>
  );
};
